import React, { useState } from "react";
import { Collapse } from "react-bootstrap";
import StatCard from "../StatCard";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/outline";

const variantStyles = {
  overview:
    "border-l-4 border-l-[#fba82c] bg-gradient-to-r from-amber-50/80 to-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]",
  amadeus:
    "border-l-4 border-l-amber-600 bg-gradient-to-r from-amber-50/60 to-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]",
  ndc: "border-l-4 border-l-sky-600 bg-gradient-to-r from-sky-50/60 to-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]",
  airarabia:
    "border-l-4 border-l-rose-600 bg-gradient-to-r from-rose-50/60 to-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]",
  default:
    "border-l-4 border-l-slate-400 bg-gradient-to-r from-slate-50/80 to-white shadow-[0_1px_3px_rgba(0,0,0,0.04)]",
};

/**
 * Renders stat groups in a grid. Cards stay fixed; expanded content appears
 * in a full-width block below the grid without shifting card positions.
 */
const GridStatGroup = ({
  groups = [],
  className = "grid-cols-1 md:grid-cols-3",
}) => {
  const [expandedId, setExpandedId] = useState(null);
  const [exitingGroup, setExitingGroup] = useState(null);

  const handleToggle = (id) => {
    if (expandedId === id) {
      setExitingGroup(groups.find((g) => g.id === id));
      setExpandedId(null);
    } else {
      setExitingGroup(null);
      setExpandedId(id);
    }
  };

  const expandedGroup = groups.find((g) => g.id === expandedId);
  const displayedGroup = expandedGroup || exitingGroup;

  return (
    <div className="mb-4">
      <div className={`grid gap-4 ${className}`}>
        {groups.map((group) => {
          const isOpen = expandedId === group.id;
          const variant = variantStyles[group.variant] || variantStyles.default;
          return (
            <div
              key={group.id}
              className={`rounded-2xl overflow-hidden border border-slate-200/90 transition-all duration-300 ease-out hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] hover:border-slate-200 ${variant}`}
            >
              <button
                type="button"
                onClick={() => handleToggle(group.id)}
                className="flex gap-3 justify-between items-center px-6 py-4 w-full text-left transition-colors duration-200 focus:outline-none hover:bg-white/60 active:bg-white/80"
              >
                <div className="flex gap-4 items-center">
                  <span
                    className={`flex items-center justify-center w-9 h-9 rounded-xl transition-transform duration-200 ${
                      isOpen ? "bg-slate-200/80" : "bg-slate-100"
                    }`}
                  >
                    {isOpen ? (
                      <ChevronDownIcon className="w-5 h-5 text-slate-600" />
                    ) : (
                      <ChevronRightIcon className="w-5 h-5 text-slate-600" />
                    )}
                  </span>
                  <div>
                    <h3 className="text-[15px] font-semibold text-slate-800 tracking-tight">
                      {group.title}
                    </h3>
                    {group.subtitle && (
                      <p className="text-[13px] text-slate-500 mt-0.5 leading-relaxed">
                        {group.subtitle}
                      </p>
                    )}
                  </div>
                  {/* {group.badge !== undefined && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-[#fba82c]/15 text-[#c4820a] tracking-wide uppercase">
                      {group.badge} metrics
                    </span>
                  )} */}
                </div>
                <span className="text-[12px] text-slate-400 font-medium hidden sm:inline">
                  {isOpen ? "Collapse" : "Expand"}
                </span>
              </button>
            </div>
          );
        })}
      </div>

      {/* Expanded content renders below grid at full width */}
      <Collapse in={!!expandedGroup} onExited={() => setExitingGroup(null)}>
        <div>
          {displayedGroup && (
            <div
              className={`rounded-2xl overflow-hidden border border-slate-200/90 mt-2 ${
                variantStyles[displayedGroup.variant] || variantStyles.default
              }`}
            >
              <div className="px-6 pt-4 pb-6">
                <div className="grid grid-cols-1 gap-4 w-full sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {displayedGroup.stats.map(
                    ({ icon, color, title: statTitle, data }, index) => (
                      <StatCard
                        key={`${statTitle}-${index}`}
                        icon={icon}
                        color={color}
                        title={statTitle}
                        data={data}
                        compact
                      />
                    )
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </Collapse>
    </div>
  );
};

export default GridStatGroup;
